import { useTranslation } from 'react-i18next';
import StarRating from '@/components/shared/StarRating';
import { Separator } from '@/components/ui/separator';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle
} from '@/components/ui/sheet';
import type { CategoryDefinition, InsurerRatingConfig } from '@/features/Quote/types/insurerRating';

interface InsurerRatingDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	insurerName: string;
	imageKey?: string;
	insurerSlug?: string;
	ratingData: InsurerRatingConfig;
	categoryDefinitions: Record<string, CategoryDefinition>;
}

const RatingBar = ({ rating }: { rating: number }) => {
	const percentage = (rating / 5) * 100;

	return (
		<div className="flex items-center gap-3 w-full">
			<div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
				<div
					className="h-full bg-amber-400 rounded-full transition-all duration-300"
					style={{ width: `${percentage}%` }}
				/>
			</div>
			<span className="text-sm font-medium text-foreground min-w-[2.5rem] text-right">
				{rating.toFixed(1)}
			</span>
		</div>
	);
};

interface CategoryRatingProps {
	label: string;
	description: string;
	rating: number;
}

const CategoryRating = ({ label, description, rating }: CategoryRatingProps) => (
	<div className="space-y-2 leading-loose">
		<span className="text-sm font-medium text-foreground">{label}</span>
		<p className="text-xs text-muted-foreground">{description}</p>
		<RatingBar rating={rating} />
	</div>
);

const InsurerRatingDrawer = ({
	open,
	onOpenChange,
	insurerName,
	imageKey,
	insurerSlug,
	ratingData,
	categoryDefinitions
}: InsurerRatingDrawerProps) => {
	const { t } = useTranslation();
	const API_URL = import.meta.env.VITE_API_BASE_URL;
	// Use imageKey for logo path if available, fallback to insurerSlug for backward compatibility
	const logoSlug = imageKey || insurerSlug;

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="overflow-y-auto w-full max-w-full sm:max-w-[540px]">
				<SheetHeader>
					<div className="flex items-center gap-4 mb-2">
						{logoSlug && (
							<div className="w-16 h-16 flex items-center justify-center bg-muted rounded-lg p-2">
								<img
									src={`${API_URL}/assets/providers/${logoSlug}/logo.png`}
									className="max-h-12 w-full object-contain"
									alt={`${insurerName} logo`}
								/>
							</div>
						)}
						<div>
							<SheetTitle className="text-xl">{ratingData.name}</SheetTitle>
							<SheetDescription>{t('quotes.rating.productType')}</SheetDescription>
						</div>
					</div>
				</SheetHeader>

				<div className="px-6 py-4">
					{/* Overall Rating */}
					<div className="mb-6">
						<StarRating rating={ratingData.overallRating} size="lg" />
						<p className="text-sm text-muted-foreground mt-2">{t('quotes.rating.overallRating')}</p>
					</div>

					<Separator className="my-6" />

					{/* Category Ratings */}
					<div className="space-y-6">
						<h3 className="text-lg font-semibold text-foreground">
							{t('quotes.rating.categoryRatings')}
						</h3>
						<div className="space-y-5">
							{Object.entries(ratingData.categoryRatings).map(([key, rating]) => {
								const definition = categoryDefinitions[key];
								if (!definition) return null;
								return (
									<CategoryRating
										key={key}
										label={definition.label}
										description={definition.description}
										rating={rating}
									/>
								);
							})}
						</div>
					</div>

					<Separator className="my-6" />

					{/* Rating Description */}
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							{t('quotes.rating.ratingDescription', { name: ratingData.name })}
						</p>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default InsurerRatingDrawer;
